// SPDX-License-Identifier: AGPL-3.0-or-later

import {
	getDraggedMarkerIndex,
	getKeyboardValue,
	getSmoothDragGrabOffset,
	getSmoothDragValue,
	getTrackClickValue,
} from '@app/features/ui/components/slider/SliderMath';
import type {MarkerState, PointerState} from '@app/features/ui/components/slider/SliderTypes';
import type React from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';

const PASSIVE_TOUCH_LISTENER_OPTIONS: AddEventListenerOptions = {passive: true};

interface UseSliderInteractionsOptions {
	disabled: boolean;
	value: number;
	stickToMarkers: boolean;
	equidistant: boolean;
	markerState: MarkerState;
	newClosestIndex: number | null;
	setNewClosestIndex: React.Dispatch<React.SetStateAction<number | null>>;
	setUncontrolledValue: React.Dispatch<React.SetStateAction<number>>;
	notifyLiveValueChange: (value: number) => void;
	onValueChange?: (value: number) => void;
	minValue: number;
	maxValue: number;
	step?: number;
	sliderRef: React.RefObject<HTMLDivElement | null>;
	setShowTooltip: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useSliderInteractions({
	disabled,
	value,
	stickToMarkers,
	equidistant,
	markerState,
	newClosestIndex,
	setNewClosestIndex,
	setUncontrolledValue,
	notifyLiveValueChange,
	onValueChange,
	minValue,
	maxValue,
	step,
	sliderRef,
	setShowTooltip,
}: UseSliderInteractionsOptions) {
	const rafRef = useRef<number | null>(null);
	const lastDragValueRef = useRef<number | null>(null);
	const lastDragMarkerIndexRef = useRef<number | null>(null);
	const dragStartMarkerIndexRef = useRef<number | null>(null);
	const dragGrabOffsetRef = useRef(0);
	const pointerStateRef = useRef<PointerState>({x: 0, boundingRect: null});
	const [isDragging, setIsDragging] = useState(false);
	const startDrag = useCallback(() => {
		setIsDragging(true);
		setShowTooltip(true);
		document.dispatchEvent(new CustomEvent('slider-drag-start'));
	}, [setShowTooltip]);
	const resetDragRefs = useCallback(() => {
		lastDragValueRef.current = null;
		lastDragMarkerIndexRef.current = null;
		dragStartMarkerIndexRef.current = null;
		pointerStateRef.current = {x: 0, boundingRect: null};
	}, []);
	const commitDrag = useCallback(() => {
		setIsDragging(false);
		setShowTooltip(false);
		document.dispatchEvent(new CustomEvent('slider-drag-end'));
		const commitValue =
			lastDragValueRef.current ??
			(stickToMarkers && newClosestIndex != null ? markerState.sortedMarkers[newClosestIndex] : value);
		if (commitValue !== undefined) {
			setUncontrolledValue(commitValue);
			onValueChange?.(commitValue);
		}
		if (stickToMarkers) {
			setNewClosestIndex(null);
		}
		resetDragRefs();
	}, [
		markerState.sortedMarkers,
		newClosestIndex,
		onValueChange,
		resetDragRefs,
		setNewClosestIndex,
		setShowTooltip,
		setUncontrolledValue,
		stickToMarkers,
		value,
	]);
	const moveSmoothly = useCallback(
		(clientX: number) => {
			const pointerState = pointerStateRef.current;
			if (!pointerState.boundingRect) return;
			const newValue = getSmoothDragValue({
				clientX,
				rect: pointerState.boundingRect,
				markerState,
				step,
				dragGrabOffset: dragGrabOffsetRef.current,
			});
			if (newValue === null) return;
			lastDragValueRef.current = newValue;
			if (rafRef.current !== null) {
				cancelAnimationFrame(rafRef.current);
			}
			rafRef.current = requestAnimationFrame(() => {
				setUncontrolledValue(newValue);
				notifyLiveValueChange(newValue);
				rafRef.current = null;
			});
		},
		[markerState, notifyLiveValueChange, setUncontrolledValue, step],
	);
	const moveStaggered = useCallback(
		(clientX: number) => {
			const startMarkerIndex = dragStartMarkerIndexRef.current ?? markerState.closestMarkerIndex;
			const pointerState = pointerStateRef.current;
			if (!pointerState.boundingRect || startMarkerIndex === undefined) return;
			const closestIndex = getDraggedMarkerIndex({
				clientX,
				startClientX: pointerState.x,
				rect: pointerState.boundingRect,
				markerState,
				startMarkerIndex,
				equidistant,
			});
			if (closestIndex === null) return;
			setNewClosestIndex(closestIndex);
			if (lastDragMarkerIndexRef.current !== closestIndex) {
				lastDragMarkerIndexRef.current = closestIndex;
				const newValue = markerState.sortedMarkers[closestIndex];
				lastDragValueRef.current = newValue;
				setUncontrolledValue(newValue);
				notifyLiveValueChange(newValue);
			}
		},
		[equidistant, markerState, notifyLiveValueChange, setNewClosestIndex, setUncontrolledValue],
	);
	const handleTrackMouseDown = useCallback(
		(event: React.MouseEvent<HTMLDivElement>) => {
			if (event.button !== 0) return;
			if (disabled) return;
			if ((event.target as HTMLElement).closest('button')) return;
			event.preventDefault();
			event.stopPropagation();
			const rect = sliderRef.current?.getBoundingClientRect();
			if (!rect) return;
			const next = getTrackClickValue({
				clientX: event.clientX,
				rect,
				markerState,
				stickToMarkers,
				step,
			});
			setUncontrolledValue(next.value);
			notifyLiveValueChange(next.value);
			lastDragValueRef.current = next.value;
			startDrag();
			pointerStateRef.current = {x: event.clientX, boundingRect: rect};
			dragGrabOffsetRef.current = 0;
			if (stickToMarkers) {
				dragStartMarkerIndexRef.current = next.markerIndex;
				lastDragMarkerIndexRef.current = next.markerIndex;
				setNewClosestIndex(next.markerIndex);
			} else {
				dragStartMarkerIndexRef.current = null;
				lastDragMarkerIndexRef.current = null;
			}
		},
		[
			disabled,
			markerState,
			notifyLiveValueChange,
			setNewClosestIndex,
			setUncontrolledValue,
			sliderRef,
			startDrag,
			step,
			stickToMarkers,
		],
	);
	const handleThumbMouseDown = useCallback(
		(event: React.MouseEvent<HTMLDivElement>) => {
			if (event.button !== 0) return;
			if (disabled) return;
			event.preventDefault();
			event.stopPropagation();
			startDrag();
			const rect = sliderRef.current?.getBoundingClientRect() ?? null;
			lastDragValueRef.current = value;
			pointerStateRef.current = {x: event.clientX, boundingRect: rect};
			dragGrabOffsetRef.current =
				rect && !stickToMarkers ? getSmoothDragGrabOffset(event.clientX, rect, value, markerState) : 0;
			if (stickToMarkers) {
				const closestMarkerIndex = markerState.closestMarkerIndex ?? null;
				dragStartMarkerIndexRef.current = closestMarkerIndex;
				lastDragMarkerIndexRef.current = closestMarkerIndex;
				setNewClosestIndex(closestMarkerIndex);
			} else {
				dragStartMarkerIndexRef.current = null;
				lastDragMarkerIndexRef.current = null;
			}
		},
		[disabled, markerState, setNewClosestIndex, sliderRef, startDrag, stickToMarkers, value],
	);
	const handleMouseMove = useCallback(
		(event: MouseEvent) => {
			event.preventDefault();
			if (stickToMarkers) {
				moveStaggered(event.clientX);
			} else {
				moveSmoothly(event.clientX);
			}
		},
		[moveSmoothly, moveStaggered, stickToMarkers],
	);
	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			if (disabled) return;
			const newValue = getKeyboardValue({
				key: event.key,
				value,
				stickToMarkers,
				markerState,
				minValue,
				maxValue,
				step,
			});
			if (newValue === null) return;
			event.preventDefault();
			setUncontrolledValue(newValue);
			onValueChange?.(newValue);
		},
		[disabled, markerState, maxValue, minValue, onValueChange, setUncontrolledValue, step, stickToMarkers, value],
	);
	const handleThumbTouchStart = useCallback(
		(event: React.TouchEvent<HTMLDivElement>) => {
			if (disabled) return;
			event.stopPropagation();
			startDrag();
			const rect = sliderRef.current?.getBoundingClientRect() ?? null;
			const touchX = event.touches[0].clientX;
			lastDragValueRef.current = value;
			pointerStateRef.current = {x: touchX, boundingRect: rect};
			dragGrabOffsetRef.current =
				rect && !stickToMarkers ? getSmoothDragGrabOffset(touchX, rect, value, markerState) : 0;
			if (stickToMarkers) {
				const closestMarkerIndex = markerState.closestMarkerIndex ?? null;
				dragStartMarkerIndexRef.current = closestMarkerIndex;
				lastDragMarkerIndexRef.current = closestMarkerIndex;
				setNewClosestIndex(closestMarkerIndex);
			} else {
				dragStartMarkerIndexRef.current = null;
				lastDragMarkerIndexRef.current = null;
			}
		},
		[disabled, markerState, setNewClosestIndex, sliderRef, startDrag, stickToMarkers, value],
	);
	const handleTouchMove = useCallback(
		(event: TouchEvent) => {
			const touch = event.touches[0];
			if (!touch) return;
			if (stickToMarkers) {
				moveStaggered(touch.clientX);
			} else {
				moveSmoothly(touch.clientX);
			}
		},
		[moveSmoothly, moveStaggered, stickToMarkers],
	);
	useEffect(() => {
		if (!isDragging) return;
		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', commitDrag);
		document.addEventListener('touchmove', handleTouchMove, PASSIVE_TOUCH_LISTENER_OPTIONS);
		document.addEventListener('touchend', commitDrag);
		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', commitDrag);
			document.removeEventListener('touchmove', handleTouchMove);
			document.removeEventListener('touchend', commitDrag);
			if (rafRef.current !== null) {
				cancelAnimationFrame(rafRef.current);
				rafRef.current = null;
			}
		};
	}, [commitDrag, handleMouseMove, handleTouchMove, isDragging]);
	return {
		isDragging,
		handleTrackMouseDown,
		handleThumbMouseDown,
		handleThumbTouchStart,
		handleKeyDown,
	};
}
